import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';

import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { KeycloakService } from 'keycloak-angular';
import { Berufsgruppe } from '../models/berufsgruppe';
import { NatPerson, Status, User } from '../models/user';
import { ApiService } from './api.service';

export const GBR_OHNE_HAFTUNGSBESCHRAENKUNG = 'GbR ohne Haftungsbeschraenkung';
const ZUSATZ_SERVICE_SELECT_LIQUID = 'select liquid';

export interface AppConfig {
  data?: {
    enableTagesgeldkonto?: boolean;
    enableWunschPinAgree?: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: User;
  redirectUrl: string;
  index = 0;

  private readonly api = inject(ApiService);
  private readonly translate = inject(TranslateService);
  private readonly keycloakService = inject(KeycloakService);

  private keycloakToken?: string;
  private berufsGruppe?: Berufsgruppe;
  private onlineBankingPublicKey?: string;
  private pendingUserRequest$?: Observable<boolean>;

  /**
   * Lädt den angemeldeten Benutzer, falls noch nicht vorhanden. Parallele Aufrufe
   * teilen sich denselben Request, statt jeweils einen neuen auszulösen.
   */
  checkLogin(): Observable<boolean> {
    if (this.user) {
      return of(true);
    }

    this.pendingUserRequest$ ??= this.api.get<User>('user').pipe(
      tap((resUser) => this.applyUser(resUser)),
      map(() => true),
      finalize(() => (this.pendingUserRequest$ = undefined)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.pendingUserRequest$;
  }

  login(uuid: string, email: string, institutions: unknown, institutionID?: string): Observable<User> {
    if (this.user) {
      return of(this.user);
    }

    return this.api
      .post<User>('user/login', {
        uuid,
        email: email.toLowerCase(),
        institutions,
        institutionID,
      })
      .pipe(map((resUser) => this.applyUser(resUser)));
  }

  refreshUser(): Observable<User> {
    return this.api
      .get<User>(`user/refreshUser/${this.user._id}`)
      .pipe(map((resUser) => this.applyUser(resUser, { keepLanguage: true })));
  }

  logout(): void {
    window.sessionStorage.clear();
    this.keycloakService.logout();
    this.index = 0;
    this.user = null as unknown as User;
    this.pendingUserRequest$ = undefined;
  }

  setKeycloakToken(token: string): void {
    this.keycloakToken = token;
  }

  getKeycloakToken(): string | undefined {
    return this.keycloakToken;
  }

  setOnlineBankingPublicKey(onlineBankingPublicKey: string): void {
    this.onlineBankingPublicKey = onlineBankingPublicKey;
  }

  getOnlineBankingPublicKey(): string | undefined {
    return this.onlineBankingPublicKey;
  }

  setBerufsGruppe(berufsGruppe: Berufsgruppe): void {
    this.berufsGruppe = berufsGruppe;
  }

  getBerufsGruppe(): Berufsgruppe | undefined {
    return this.berufsGruppe;
  }

  isJurPerson(): boolean {
    return !!this.user?.isJurPerson;
  }

  isRechtsformGBR(): boolean {
    return this.user?.JurPerson?.JurDaten?.GbrArt === GBR_OHNE_HAFTUNGSBESCHRAENKUNG;
  }

  findFlaggedNaturalPerson(natPersonen: NatPerson[] = []): number {
    const index = natPersonen.findIndex((natPerson) => natPerson.Person?.VollmachtKontoeroeffnung);
    return index === -1 ? 0 : index;
  }

  isSelectLiquid(natPersonen: NatPerson[] = this.user?.natPersonen): boolean {
    if (!natPersonen?.length) {
      return false;
    }
    const index = this.findFlaggedNaturalPerson(natPersonen);
    const zusatzService = (natPersonen[index]?.BranchenInfo?.ZusatzService ?? '').toString().toLowerCase().trim();
    return zusatzService === ZUSATZ_SERVICE_SELECT_LIQUID;
  }

  updateFinapiStatus(userId: string): Observable<unknown> {
    return this.api.post('user/update-finapi-status', { userId });
  }

  updateQuestions(userId: string): Observable<unknown> {
    return this.api.post('user/updateQuestions', { userid: userId });
  }

  updateAuswahlProdukt(userId: string): Observable<unknown> {
    return this.api.post('user/updateauswahlprodukt', { userid: userId });
  }

  changeStatus(userId: string, status: Status): Observable<User> {
    return this.api.post<User>('user/changeStatus', { userid: userId, status });
  }

  getConfig(): Observable<AppConfig> {
    return this.api.get<AppConfig>('user/getConfig');
  }

  hasKontowechselserviceSelected(): Observable<boolean> {
    return this.api.get<boolean>(`user/has-kontowechservice-selected/${this.user._id}`);
  }

  private applyUser(resUser: User, options: { keepLanguage?: boolean } = {}): User {
    this.user = new User(resUser);
    this.index = this.findFlaggedNaturalPerson(this.user.natPersonen);
    if (!options.keepLanguage) {
      this.translate.use('odfin');
    }
    return this.user;
  }
}
